import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReferralSystem = ({ referralData, onInviteFriend, onClaimReward }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [shareMethod, setShareMethod] = useState('email');

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const handleInvite = () => {
    const contact = shareMethod === 'email' ? inviteEmail : invitePhone;
    if (contact?.trim()) {
      onInviteFriend(contact, shareMethod);
      setInviteEmail('');
      setInvitePhone('');
    }
  };

  const handleShare = (platform) => {
    const referralUrl = `https://autocasse.cm/register?ref=${referralData?.referralCode}`;
    const message = `Join AutoCasse and get premium features! Use my referral code: ${referralData?.referralCode}`;
    
    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(message + ' ' + referralUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(referralUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard?.writeText(referralUrl);
        break;
    }
  };

  const rewardTiers = [
    {
      referrals: 1,
      reward: 'Free 1-month department visibility',
      icon: 'MapPin',
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/20'
    },
    {
      referrals: 3,
      reward: '50% discount on premium features',
      icon: 'Percent',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent/20'
    },
    {
      referrals: 5,
      reward: 'Free 3-month regional visibility',
      icon: 'Map',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20'
    },
    {
      referrals: 10,
      reward: 'Free 6-month national visibility',
      icon: 'Globe2',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-200'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground mb-2 flex items-center">
          <Icon name="Users" size={24} className="mr-2 text-primary" />
          Referral Program
        </h2>
        <p className="text-muted-foreground">Invite friends and earn rewards for your AutoCasse account</p>
      </div>
      {/* Referral Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg text-center">
          <Icon name="Users" size={24} className="mx-auto text-primary mb-2" />
          <p className="text-2xl font-bold text-foreground">{referralData?.totalReferrals}</p>
          <p className="text-sm text-muted-foreground">Total Referrals</p>
        </div>
        
        <div className="p-4 bg-success/5 border border-success/20 rounded-lg text-center">
          <Icon name="UserCheck" size={24} className="mx-auto text-success mb-2" />
          <p className="text-2xl font-bold text-foreground">{referralData?.activeReferrals}</p>
          <p className="text-sm text-muted-foreground">Active Users</p>
        </div>
        
        <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg text-center">
          <Icon name="Gift" size={24} className="mx-auto text-accent mb-2" />
          <p className="text-2xl font-bold text-foreground">{referralData?.rewardsEarned}</p>
          <p className="text-sm text-muted-foreground">Rewards Earned</p>
        </div>
        
        <div className="p-4 bg-muted/50 border border-border rounded-lg text-center">
          <Icon name="Clock" size={24} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-2xl font-bold text-foreground">{referralData?.pendingRewards}</p>
          <p className="text-sm text-muted-foreground">Pending Rewards</p>
        </div>
      </div>
      {/* Referral Code */}
      <div className="mb-6 p-4 bg-muted/25 rounded-lg">
        <h3 className="font-medium text-foreground mb-3">Your Referral Code</h3>
        <div className="flex items-center space-x-3">
          <div className="flex-1 p-3 bg-card border border-border rounded font-mono text-lg font-bold text-center text-primary">
            {referralData?.referralCode}
          </div>
          <Button
            variant="outline"
            onClick={() => handleShare('copy')}
            iconName="Copy"
            iconSize={16}
            title="Copy referral code"
          />
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Share this code with friends to earn rewards when they sign up and make their first purchase
        </p>
      </div>
      {/* Invite Friends */}
      <div className="mb-6">
        <h3 className="font-medium text-foreground mb-4">Invite Friends</h3>
        
        {/* Invite Method Toggle */}
        <div className="flex space-x-2 mb-4">
          <Button
            variant={shareMethod === 'email' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShareMethod('email')}
            iconName="Mail"
            iconPosition="left"
            iconSize={14}
          >
            Email
          </Button>
          <Button
            variant={shareMethod === 'phone' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShareMethod('phone')}
            iconName="Phone"
            iconPosition="left"
            iconSize={14}
          >
            Phone
          </Button>
        </div>

        {/* Invite Form */}
        <div className="flex space-x-3">
          {shareMethod === 'email' ? (
            <Input
              type="email"
              placeholder="Enter friend's email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e?.target?.value)}
              className="flex-1"
            />
          ) : (
            <Input
              type="tel"
              placeholder="Enter friend's phone (6XXXXXXXX)"
              value={invitePhone}
              onChange={(e) => setInvitePhone(e?.target?.value)}
              className="flex-1"
            />
          )}
          <Button
            variant="default"
            onClick={handleInvite}
            disabled={shareMethod === 'email' ? !inviteEmail?.trim() : !invitePhone?.trim()}
            iconName="Send"
            iconPosition="left"
            iconSize={16}
          >
            Send Invite
          </Button>
        </div>
      </div>
      {/* Social Sharing */}
      <div className="mb-6">
        <h3 className="font-medium text-foreground mb-4">Share on Social Media</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button
            variant="outline"
            onClick={() => handleShare('whatsapp')}
            iconName="MessageCircle"
            iconPosition="left"
            iconSize={16}
            className="text-green-600 border-green-200 hover:bg-green-50"
          >
            WhatsApp
          </Button>
          <Button
            variant="outline"
            onClick={() => handleShare('facebook')}
            iconName="Facebook"
            iconPosition="left"
            iconSize={16}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            Facebook
          </Button>
          <Button
            variant="outline"
            onClick={() => handleShare('twitter')}
            iconName="Twitter"
            iconPosition="left"
            iconSize={16}
            className="text-sky-600 border-sky-200 hover:bg-sky-50"
          >
            Twitter
          </Button>
          <Button
            variant="outline"
            onClick={() => handleShare('copy')}
            iconName="Link"
            iconPosition="left"
            iconSize={16}
          >
            Copy Link
          </Button>
        </div>
      </div>
      {/* Reward Tiers */}
      <div className="mb-6">
        <h3 className="font-medium text-foreground mb-4">Reward Tiers</h3>
        <div className="space-y-3">
          {rewardTiers?.map((tier, index) => (
            <div
              key={index}
              className={`p-4 border rounded-lg ${tier?.bgColor} ${tier?.borderColor} ${
                referralData?.totalReferrals >= tier?.referrals ? 'opacity-100' : 'opacity-60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tier?.bgColor}`}>
                    <Icon name={tier?.icon} size={20} className={tier?.color} />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{tier?.referrals} Referral{tier?.referrals > 1 ? 's' : ''}</p>
                    <p className="text-sm text-muted-foreground">{tier?.reward}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {referralData?.totalReferrals >= tier?.referrals ? (
                    <div className="flex items-center space-x-1 text-success">
                      <Icon name="CheckCircle" size={16} />
                      <span className="text-sm font-medium">Unlocked</span>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      {tier?.referrals - referralData?.totalReferrals} more needed
                    </div>
                  )}
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-3">
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      referralData?.totalReferrals >= tier?.referrals ? 'bg-success' : tier?.color?.replace('text-', 'bg-')
                    }`}
                    style={{ 
                      width: `${Math.min(100, (referralData?.totalReferrals / tier?.referrals) * 100)}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Available Rewards */}
      {referralData?.availableRewards && referralData?.availableRewards?.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium text-foreground mb-4">Available Rewards</h3>
          <div className="space-y-3">
            {referralData?.availableRewards?.map((reward) => (
              <div
                key={reward?.id}
                className="flex items-center justify-between p-4 border border-success/20 bg-success/5 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <Icon name="Gift" size={20} className="text-success" />
                  <div>
                    <p className="font-medium text-foreground">{reward?.title}</p>
                    <p className="text-sm text-muted-foreground">{reward?.description}</p>
                    <p className="text-xs text-muted-foreground">
                      Expires: {new Date(reward.expiryDate)?.toLocaleDateString('fr-CM')}
                    </p>
                  </div>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => onClaimReward(reward?.id)}
                  iconName="Gift"
                  iconPosition="left"
                  iconSize={14}
                >
                  Claim
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Recent Referrals */}
      {referralData?.recentReferrals && referralData?.recentReferrals?.length > 0 && (
        <div>
          <h3 className="font-medium text-foreground mb-4">Recent Referrals</h3>
          <div className="space-y-2">
            {referralData?.recentReferrals?.slice(0, 5)?.map((referral) => (
              <div
                key={referral?.id}
                className="flex items-center justify-between p-3 bg-muted/25 rounded"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{referral?.name || 'Anonymous User'}</p>
                    <p className="text-xs text-muted-foreground">
                      Joined {new Date(referral.joinDate)?.toLocaleDateString('fr-CM')}
                    </p>
                  </div>
                </div>
                <div className={`text-xs font-medium ${
                  referral?.status === 'active' ? 'text-success' : 
                  referral?.status === 'pending' ? 'text-warning' : 'text-muted-foreground'
                }`}>
                  {referral?.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralSystem;